<template>
  <div class="space-y-3 pb-2">
    <div class="space-y-1.5 text-sm">
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Customer</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">{{ customerName }}</span>
      </div>
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Order Name</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">{{
          props.order.name || '--'
        }}</span>
      </div>
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Order Ref</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">
          {{ props.order.reference || '--' }}
        </span>
      </div>
    </div>

    <div class="h-[1px] w-full bg-grey-light-200" />

    <div class="flex items-center justify-between">
      <LabelComponent>Allow editing</LabelComponent>
      <SwitchComponent v-model="allowEditing" class="!mt-0" />
    </div>

    <div class="space-y-2">
      <LabelComponent>Edit Total</LabelComponent>
      <div class="relative">
        <span v-if="allowEditing" class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-500">R</span>
        <Input
          v-model="editedAmount"
          type="text"
          class="pl-7"
          :disabled="!allowEditing"
          @input="validateEditAmount"
          placeholder="Enter new total"
        />
      </div>
      <div class="flex items-center justify-between text-grey-light-700">
        <span>Current Order Total</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(originalOrderTotal) }}</span>
      </div>
      <div
        class="flex items-center justify-between"
        :class="hasEditedAmount ? 'text-brand-700' : 'text-grey-light-700'"
      >
        <span>Total Paid</span>
        <span class="font-semibold" :class="hasEditedAmount ? 'text-brand-700' : 'text-grey-light-900'">
          {{ displayCurrency(totalPaid) }}
        </span>
      </div>
    </div>

    <div class="space-y-2" v-if="props.useTravelDate">
      <LabelComponent>Edit Travel Date</LabelComponent>
      <Popover v-model:open="showTravelDatePicker">
        <PopoverTrigger as-child>
          <Button
            class="w-full justify-start text-left font-normal"
            :class="cn(!selectedTravelDate && 'text-muted-foreground')"
            variant="outline"
            :disabled="!allowEditing"
          >
            <CalendarIcon class="mr-2 h-4 w-4" />
            {{ selectedTravelDate ? travelDateFormatted : 'Select date' }}
          </Button>
        </PopoverTrigger>

        <PopoverContent class="w-auto">
          <Calendar
            v-model="selectedTravelDate"
            :weekday-format="'short'"
            class="rounded-md"
            @update:modelValue="onTravelDateChange"
          />
        </PopoverContent>
      </Popover>
      <div
        class="flex items-center justify-between"
        :class="hasSelectedTravelDate ? 'text-brand-700' : 'text-grey-light-700'"
      >
        <span>{{ hasSelectedTravelDate ? 'New Travel Date' : 'Current Travel Date' }}</span>
        <div class="flex items-center justify-end gap-1">
          <span class="font-semibold" :class="hasSelectedTravelDate ? 'text-brand-700' : 'text-grey-light-900'">
            {{
              hasSelectedTravelDate && selectedTravelDate
                ? displayDate(selectedTravelDate.toString())
                : props.travelDate
                  ? displayDate(props.travelDate)
                  : 'Select date'
            }}
          </span>
        </div>
      </div>
      <div class="flex items-center justify-between text-grey-light-700">
        <span>{{ hasSelectedTravelDate ? 'New Final Payment Date' : 'Current Final Payment Date' }}</span>
        <span class="font-semibold" :class="hasSelectedTravelDate ? 'text-brand-700' : 'text-grey-light-900'">
          {{ displayDate(amendmentEndDate) }}
        </span>
      </div>
      <div
        v-if="travelDateValidationError"
        class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
      >
        {{ travelDateValidationError }}
      </div>
    </div>
    <Button
      class="w-full"
      :disabled="
        !allowEditing ||
        (!hasEditedAmount && !hasSelectedTravelDate) ||
        !!travelDateValidationError ||
        loadingPreview ||
        loadingMerchantFeatures
      "
      @click="onPreviewPlan"
    >
      <LayupSpinner v-if="loadingPreview" />
      <span v-else> Create Preview</span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import { cn } from '@/lib/utils';
import { previewAmendment } from '@/services/amendment';
import { useMerchantStore } from '@/stores/merchants';
import { isNumber } from '@/utils/form.utils';
import { displayCurrency, displayDate, toCents } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { parseDate, type DateValue } from '@internationalized/date';
import dayjs from 'dayjs';
import { Calendar as CalendarIcon } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import type { ModelOrderAmendmentPreviewResponse, ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  order: Partial<ModelOrderResponse>;
  endDate: string;
  travelDate: string;
  useTravelDate: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:can-create-plan', value: boolean): void;
  (e: 'update:error', value: string): void;
  (e: 'preview-created', value: ModelOrderAmendmentPreviewResponse): void;
}>();

const merchantStore = useMerchantStore();

const loadingPreview = ref(false);
const loadingMerchantFeatures = ref(false);
const allowEditing = ref(false);
const editedAmount = ref('');
const editedTravelDate = ref('');
const originalOrderTotal = ref(0);
const showTravelDatePicker = ref(false);
const hasSelectedTravelDate = ref(false);
const customerName = computed(() => props.order.plans?.[0]?.benefactor?.name || '--');
const totalPaid = computed(() => {
  const plans = props.order.plans ?? [];
  return plans.reduce((sum, plan: any) => {
    const paidTotal = (plan.payments ?? []).reduce((paymentSum: number, payment: any) => {
      return paymentSum + (payment.paid ? payment.amount || 0 : 0);
    }, 0);
    return sum + paidTotal;
  }, 0);
});
const hasEditedAmount = computed(() => Number(editedAmount.value) > 0);
const canPreview = computed(() => hasEditedAmount.value || hasSelectedTravelDate.value);

const toIsoDateString = (value?: string) => {
  if (!value) return '';

  // Already in ISO date format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // South African date format (DD/MM/YYYY)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/');
    return `${year}-${month}-${day}`;
  }

  // Keep the local calendar date instead of normalizing to UTC, which can shift by a day.
  const date = dayjs(value);
  if (!date.isValid()) return '';
  return date.format('YYYY-MM-DD');
};

const getInitialTravelDate = (): DateValue | undefined => {
  const isoDate = toIsoDateString(props.travelDate);
  return isoDate ? parseDate(isoDate) : undefined;
};

const selectedTravelDate = ref(getInitialTravelDate()) as Ref<DateValue | undefined>;

const travelDateFormatted = computed(() =>
  selectedTravelDate.value ? displayDate(selectedTravelDate.value.toString()) : '',
);

const parseTravelDayOffset = () => {
  const rawOffset = merchantStore.merchant?.features?.numberOfDayAfterEndDateToTravel;
  if (rawOffset == null) return undefined;

  const normalizedOffset = typeof rawOffset === 'number' ? rawOffset : Number(rawOffset);
  if (!Number.isFinite(normalizedOffset)) return undefined;
  return Math.max(0, normalizedOffset);
};

const hasLoadedTravelDayOffset = computed(() => {
  return parseTravelDayOffset() !== undefined;
});

const getTravelDayOffset = () => {
  return parseTravelDayOffset() ?? 0;
};

const minimumAllowedEndDate = computed(() => {
  return dayjs().add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD');
});

const ensureMerchantTravelDateFeatureLoaded = async () => {
  if (hasLoadedTravelDayOffset.value) return;
  if (!merchantStore.activeMerchantId || loadingMerchantFeatures.value) return;

  try {
    loadingMerchantFeatures.value = true;
    await merchantStore.onChangeMerchant(merchantStore.activeMerchantId);
  } catch (error) {
    createException(error);
    addMetric('create-amendment-merchant-refresh', {
      status: 'error',
      channel: 'merchant',
      method: 'onChangeMerchant',
    });
  } finally {
    loadingMerchantFeatures.value = false;
  }
};

const amendmentEndDate = computed(() => {
  const fallbackEndDate = toIsoDateString(props.endDate) || props.endDate;
  const fallbackEndDateValue = dayjs(fallbackEndDate);
  const minEndDateIso = toIsoDateString(minimumAllowedEndDate.value);
  const minEndDateValue = minEndDateIso ? dayjs(minEndDateIso) : null;

  const clampToMinEndDate = (candidateEndDate: dayjs.Dayjs) => {
    if (minEndDateValue && minEndDateValue.isValid() && candidateEndDate.isBefore(minEndDateValue, 'day')) {
      return minEndDateValue.format('YYYY-MM-DD');
    }

    return candidateEndDate.format('YYYY-MM-DD');
  };

  if (!(props.useTravelDate && hasSelectedTravelDate.value && editedTravelDate.value)) {
    if (!fallbackEndDateValue.isValid()) return fallbackEndDate;
    return clampToMinEndDate(fallbackEndDateValue);
  }

  if (!hasLoadedTravelDayOffset.value) {
    if (!fallbackEndDateValue.isValid()) return fallbackEndDate;
    return clampToMinEndDate(fallbackEndDateValue);
  }

  const selectedTravelDateIso = toIsoDateString(editedTravelDate.value);
  if (!selectedTravelDateIso) {
    if (!fallbackEndDateValue.isValid()) return fallbackEndDate;
    return clampToMinEndDate(fallbackEndDateValue);
  }

  const currentTravelDateIso = toIsoDateString(props.travelDate);
  const currentTravelDateValue = currentTravelDateIso ? dayjs(currentTravelDateIso) : null;
  const isNewTravelDateBeforeCurrent =
    currentTravelDateValue?.isValid() && dayjs(selectedTravelDateIso).isBefore(currentTravelDateValue, 'day');

  // New travel date before current → endDate = newTravelDate - 1 day
  // New travel date after current → endDate stays as original
  if (isNewTravelDateBeforeCurrent) {
    const calculatedEndDate = dayjs(selectedTravelDateIso).subtract(
      merchantStore?.merchant?.features?.numberOfDayAfterEndDateToTravel || 1,
      'day',
    );
    if (!calculatedEndDate.isValid()) {
      if (!fallbackEndDateValue.isValid()) return fallbackEndDate;
      return clampToMinEndDate(fallbackEndDateValue);
    }
    return clampToMinEndDate(calculatedEndDate);
  }

  if (!fallbackEndDateValue.isValid()) return fallbackEndDate;
  return clampToMinEndDate(fallbackEndDateValue);
});

const travelDateValidationError = computed(() => {
  if (!(props.useTravelDate && hasSelectedTravelDate.value && editedTravelDate.value)) {
    return '';
  }

  if (!hasLoadedTravelDayOffset.value) {
    return '';
  }

  const selectedTravelDateIso = toIsoDateString(editedTravelDate.value);
  if (!selectedTravelDateIso) {
    return '';
  }

  const effectiveEndDate = dayjs(amendmentEndDate.value);
  if (!effectiveEndDate.isValid()) {
    return '';
  }

  const daysAfterEndDateToTravel = getTravelDayOffset();
  const requiredEndDate = dayjs(selectedTravelDateIso).subtract(daysAfterEndDateToTravel, 'day');
  if (!requiredEndDate.isValid()) {
    return '';
  }

  if (!effectiveEndDate.isAfter(requiredEndDate, 'day')) {
    return '';
  }

  return `Final payment date must be at least ${daysAfterEndDateToTravel} ${daysAfterEndDateToTravel === 1 ? 'day' : 'days'} before the travel date.`;
});

const validateAndEmitCanCreatePlan = () => {
  emit('update:can-create-plan', allowEditing.value && canPreview.value && !travelDateValidationError.value);
};

const onPreviewPlan = async () => {
  if (!allowEditing.value) return;

  await ensureMerchantTravelDateFeatureLoaded();
  if (props.useTravelDate && hasSelectedTravelDate.value && !hasLoadedTravelDayOffset.value) {
    emit('update:error', 'Unable to load merchant travel date settings. Please try again.');
    return;
  }

  const editedAmountCents = hasEditedAmount.value ? toCents(Number(editedAmount.value)) : undefined;
  const amendedProducts = Array.isArray(props.order.products)
    ? props.order.products.map((product) => ({ ...product }))
    : [];

  if (editedAmountCents != null && amendedProducts.length === 1) {
    amendedProducts[0].amount = editedAmountCents;
  }

  if (!props.order._id) return;

  try {
    loadingPreview.value = true;

    const amendmentPayload: {
      orderId: string;
      amendmentType: 'MULTIPLE:UPDATE';
      endDate: string;
      products: typeof props.order.products;
      travelDate?: string;
    } = {
      orderId: props.order._id,
      amendmentType: 'MULTIPLE:UPDATE',
      endDate: amendmentEndDate.value,
      products: amendedProducts,
    };

    if (
      props.useTravelDate &&
      hasSelectedTravelDate.value &&
      editedTravelDate.value &&
      !travelDateValidationError.value
    ) {
      amendmentPayload.travelDate = editedTravelDate.value;
    }

    const preview = await previewAmendment(amendmentPayload);
    emit('preview-created', preview as ModelOrderAmendmentPreviewResponse);
  } catch (error: any) {
    if (error.response?.data) {
      const serverMessage = error.response?.data || 'Unknown error';
      emit('update:error', serverMessage as string);
    }

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create-order-amendment-preview', {
        status: 'network_error',
        channel: 'merchant',
        method: 'previewAmendment',
      });
    } else {
      createException(error);
      addMetric('create-order-amendment-preview', {
        status: 'error',
        channel: 'merchant',
        method: 'previewAmendment',
      });
    }
  } finally {
    loadingPreview.value = false;
  }
};

onMounted(async () => {
  await ensureMerchantTravelDateFeatureLoaded();
  originalOrderTotal.value = Number(props.order.amountDue || 0);
});

onUnmounted(() => emit('update:error', ''));

watch([allowEditing, editedAmount, editedTravelDate], () => validateAndEmitCanCreatePlan(), { immediate: true });

watch(
  () => merchantStore.activeMerchantId,
  async () => {
    await ensureMerchantTravelDateFeatureLoaded();
  },
);

watch(
  selectedTravelDate,
  (newVal) => {
    if (newVal) {
      editedTravelDate.value = newVal.toString();
    } else {
      editedTravelDate.value = '';
    }
    showTravelDatePicker.value = false;
  },
  { immediate: true },
);

const onTravelDateChange = () => {
  if (selectedTravelDate.value) {
    editedTravelDate.value = selectedTravelDate.value.toString();
    hasSelectedTravelDate.value = true;
  }
};

const validateEditAmount = (event: Event) => {
  const inputValue = isNumber(event);
  editedAmount.value = inputValue;
};
</script>
